import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import ReactMarkdown from "react-markdown";
import { Globe } from "lucide-react";
import { QuizEngine } from "@/components/resources/QuizEngine";
import type { Subtopic } from "./ELearningDashboard";

const GOLD = "#CEAB65";

// === Progress helpers (localStorage) ===
function progressKey(catId: string, modId: string) {
  return `elearn:progress:${catId}:${modId}`;
}
function setProgress(catId: string, modId: string, payload: { completed?: boolean; score?: number }) {
  try {
    const prev = localStorage.getItem(progressKey(catId, modId));
    const prevObj = prev ? JSON.parse(prev) : {};
    const next = { ...prevObj, ...payload, ts: Date.now() };
    localStorage.setItem(progressKey(catId, modId), JSON.stringify(next));
  } catch {}
}
function setLastOpened(catId: string, modId: string) {
  try {
    localStorage.setItem("elearn:last_opened", JSON.stringify({ catId, modId, ts: Date.now() }));
  } catch {}
}

function LanguageToggle({
  lang,
  setLang,
}: {
  lang: "fr" | "en";
  setLang: (l: "fr" | "en") => void;
}) {
  return (
    <div className="flex select-none items-center overflow-hidden rounded-xl border border-white/10 bg-white/10 text-xs">
      {(["fr", "en"] as const).map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          className={`flex items-center gap-1 px-2 py-1.5 transition ${
            lang === l ? "bg-white/15" : "hover:bg-white/5"
          }`}
        >
          <Globe className="h-3.5 w-3.5" /> {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

const text = {
  fr: {
    title: "E‑Learning",
    loading: "Chargement…",
    error: "Erreur",
    notFound: "Module non trouvé",
    comingSoon: "(Contenu à venir)",
    mark_done: "Marquer comme terminé",
  },
  en: {
    title: "E‑Learning",
    loading: "Loading…",
    error: "Error",
    notFound: "Module not found",
    comingSoon: "(Content coming soon)",
    mark_done: "Mark as completed",
  },
};

type ModuleData = Subtopic & {
  video_url?: string;
  quiz?: Array<{
    q_fr: string;
    q_en: string;
    choices_fr: string[];
    choices_en: string[];
    answer: number;
    explain_fr?: string;
    explain_en?: string;
  }>;
  content_md_fr?: string;
  content_md_en?: string;
};

export default function ModulePage() {
  const { categoryId, moduleId } = useParams();
  const [lang, setLang] = useState<"fr" | "en">("en");
  const t = text[lang];
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [mod, setMod] = useState<ModuleData | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const ref = doc(db, "elearning_categories", String(categoryId), "modules", String(moduleId));
        const snap = await getDoc(ref);
        if (!snap.exists()) throw new Error(t.notFound);
        const data = snap.data() as any;
        const title = lang === "fr" ? data.title_fr ?? data.title : data.title_en ?? data.title;
        const description = lang === "fr" ? data.description_fr ?? data.description : data.description_en ?? data.description;
        setMod({
          id: data.slug || String(moduleId),
          title,
          description,
          status: data.status || "ready",
          type: (data.type as any) || "course",
          video_url: data.video_url,
          quiz: data.quiz,
          content_md_fr: data.content_md_fr,
          content_md_en: data.content_md_en,
          categoryId: String(categoryId),
        });
        setLastOpened(String(categoryId), String(moduleId));
      } catch (e: any) {
        setErr(e.message || t.error);
      } finally {
        setLoading(false);
      }
    })();
  }, [categoryId, moduleId, lang]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1D314D] via-[#2E4663] to-[#0f172a] text-white" style={{ fontFamily: 'Geist, system-ui, sans-serif' }}>
      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold">{mod?.title || t.title}</h1>
          <LanguageToggle lang={lang} setLang={setLang} />
        </div>
        {loading && <p className="text-white/70">{t.loading}</p>}
        {err && <p className="text-red-200">{err}</p>}
        {mod && <ModuleRenderer lang={lang} mod={mod} />}
      </div>
    </div>
  );
}

function MarkDoneButton({ mod, lang }: { mod: ModuleData; lang: "fr" | "en" }) {
  return (
    <button
      onClick={() => setProgress(String(mod.categoryId), String(mod.id), { completed: true })}
      className="mt-4 rounded-xl px-3 py-2 font-semibold text-black transition hover:brightness-110"
      style={{ backgroundColor: GOLD }}
    >
      {lang === "fr" ? text.fr.mark_done : text.en.mark_done}
    </button>
  );
}

function ModuleRenderer({ mod, lang }: { mod: ModuleData; lang: "fr" | "en" }) {
  const md = lang === "fr" ? mod.content_md_fr : mod.content_md_en;
  
  if (mod.type === "video" && mod.video_url) {
    return (
      <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
        <div className="aspect-video w-full overflow-hidden rounded-xl">
          <iframe 
            src={mod.video_url} 
            title={mod.title} 
            className="h-full w-full" 
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowFullScreen 
          />
        </div>
        {md && (
          <div className="prose prose-invert mt-4 max-w-none">
            <ReactMarkdown>{md}</ReactMarkdown>
          </div>
        )}
        <MarkDoneButton mod={mod} lang={lang} />
      </div>
    );
  }
  
  if (mod.type === "quiz" && Array.isArray(mod.quiz)) {
    return (
      <QuizEngine
        quiz={mod.quiz}
        quizId={`${mod.categoryId}-${mod.id}`}
        lang={lang}
        durationSec={180}
        onSubmit={(score: number) => setProgress(String(mod.categoryId), String(mod.id), { completed: true, score })}
      />
    );
  }
  
  // default: course (markdown)
  return (
    <div className="prose prose-invert max-w-none">
      {md ? (
        <ReactMarkdown>{md}</ReactMarkdown>
      ) : (
        <p className="text-white/70">{text[lang].comingSoon}</p>
      )}
      <MarkDoneButton mod={mod} lang={lang} />
    </div>
  );
}
